// The streets around a traffic control work area, from OpenStreetMap (Ryan, Oct 30 2026 — the 🤖 AI
// plan). Plain geometry, no AI: which street the work is on, which way traffic comes at it, and the
// approach traced along that street to the centre of the lane (or the edge line) being closed — the
// same line a person traces by hand. The AI only picks the setup; this draws the road.
import { tcpNumbers } from './tcpLayout';

const FT_PER_M = 3.28084;

export const centroid = area => area.reduce((a, ll) => [a[0] + ll[0] / area.length, a[1] + ll[1] / area.length], [0, 0]);

// Every road within `radiusM` of the work area: [{ id, tags, nodes, geom: [[lat,lng]…] }]. The street
// map (OpenStreetMap's Overpass service) is asked through our server — it turns browsers away.
// `get` = apiFetch.
export async function fetchStreets(area, get, radiusM = 800) {
  const [lat, lng] = centroid(area);
  const d = await get(`/traffic-plans/streets?lat=${lat.toFixed(6)}&lng=${lng.toFixed(6)}&r=${radiusM}`);
  return Array.isArray(d?.ways) ? d.ways : [];
}

// A flat frame in feet around a point (as tcpLayout uses).
function frame([lat0, lng0]) {
  const kx = Math.cos((lat0 * Math.PI) / 180) * 111320 * FT_PER_M, ky = 110540 * FT_PER_M;
  return { to: ([lat, lng]) => ({ x: (lng - lng0) * kx, y: (lat - lat0) * ky }), back: ({ x, y }) => [lat0 + y / ky, lng0 + x / kx] };
}

// Stations along a polyline (feet), and where a point falls on it: station, and offset (+ = RIGHT of travel).
function measure(pts) {
  const cum = [0];
  for (let i = 1; i < pts.length; i++) cum.push(cum[i - 1] + Math.hypot(pts[i].x - pts[i - 1].x, pts[i].y - pts[i - 1].y));
  return cum;
}
function project(pts, cum, q) {
  let best = null;
  for (let i = 1; i < pts.length; i++) {
    const a = pts[i - 1], b = pts[i], L = cum[i] - cum[i - 1] || 1e-9, ux = (b.x - a.x) / L, uy = (b.y - a.y) / L;
    const t = Math.max(0, Math.min(L, (q.x - a.x) * ux + (q.y - a.y) * uy));
    const px = a.x + ux * t, py = a.y + uy * t, d = Math.hypot(q.x - px, q.y - py);
    if (!best || d < best.d) best = { d, s: cum[i - 1] + t, off: uy * (q.x - a.x) - ux * (q.y - a.y) };
  }
  return best;
}
function pointAt(pts, cum, s, off) {
  let i = 1; while (i < pts.length - 1 && cum[i] < s) i++;
  const a = pts[i - 1], b = pts[i], L = cum[i] - cum[i - 1] || 1e-9, ux = (b.x - a.x) / L, uy = (b.y - a.y) / L, t = s - cum[i - 1];
  return { x: a.x + ux * t + uy * off, y: a.y + uy * t - ux * off };
}

const nameKey = w => w.tags.name || w.tags.ref || `#${w.id}`;
const isOneway = w => ['yes', 'true', '1', '-1'].includes(String(w.tags.oneway || '')) || /^motorway/.test(w.tags.highway || '') || w.tags.junction === 'roundabout';

// The whole street (same name) joined end to end through the way the work is on, in one direction.
function chainFor(ways, start) {
  const key = nameKey(start);
  const same = ways.filter(w => w !== start && nameKey(w) === key && w.tags.highway !== 'service');
  const flip = w => ({ nodes: w.nodes.slice().reverse(), geom: w.geom.slice().reverse() });
  let cur = String(start.tags.oneway) === '-1' ? flip(start) : { nodes: start.nodes.slice(), geom: start.geom.slice() };
  const used = new Set([start.id]);
  for (const forward of [true, false]) {
    for (let k = 0; k < 60; k++) {
      const end = forward ? cur.nodes[cur.nodes.length - 1] : cur.nodes[0];
      const opts = [];
      for (const w of same) {
        if (used.has(w.id)) continue;
        const o = isOneway(w), rev = String(w.tags.oneway) === '-1';
        const first = rev ? w.nodes[w.nodes.length - 1] : w.nodes[0], last = rev ? w.nodes[0] : w.nodes[w.nodes.length - 1];
        const g = rev ? flip(w) : { nodes: w.nodes, geom: w.geom };
        if (forward) {
          if (first === end) opts.push({ w, g });
          else if (!o && last === end) opts.push({ w, g: flip(w) });
        } else {
          if (last === end) opts.push({ w, g });
          else if (!o && first === end) opts.push({ w, g: flip(w) });
        }
      }
      if (!opts.length) break;
      // Straightest carry-on wins at a fork.
      const dir = (a, b) => Math.atan2(b[0] - a[0], b[1] - a[1]);
      const gl = cur.geom;
      const here = forward ? dir(gl[gl.length - 2], gl[gl.length - 1]) : dir(gl[1], gl[0]);
      opts.sort((p, q) => {
        const tp = forward ? dir(p.g.geom[0], p.g.geom[1]) : dir(p.g.geom[p.g.geom.length - 1], p.g.geom[p.g.geom.length - 2]);
        const tq = forward ? dir(q.g.geom[0], q.g.geom[1]) : dir(q.g.geom[q.g.geom.length - 1], q.g.geom[q.g.geom.length - 2]);
        const da = x => Math.abs(Math.atan2(Math.sin(x - here), Math.cos(x - here)));
        return da(tp) - da(tq);
      });
      const pick = opts[0];
      used.add(pick.w.id);
      cur = forward
        ? { nodes: [...cur.nodes, ...pick.g.nodes.slice(1)], geom: [...cur.geom, ...pick.g.geom.slice(1)] }
        : { nodes: [...pick.g.nodes.slice(0, -1), ...cur.nodes], geom: [...pick.g.geom.slice(0, -1), ...cur.geom] };
    }
  }
  return cur.geom;
}

const lanesOf = w => { const n = parseInt(w.tags.lanes, 10); return Number.isFinite(n) && n > 0 ? n : (isOneway(w) ? 1 : 2); };
const mph = w => { const m = /(\d+)\s*mph/i.exec(String(w.tags.maxspeed || '')); return m ? Number(m[1]) : null; };
const compass = deg => ['Northbound', 'Eastbound', 'Southbound', 'Westbound'][Math.round((((deg % 360) + 360) % 360) / 90) % 4];

// The streets near the work, numbered for the AI's picture, nearest first, with what the geometry says
// about each: how far the work sits from its centre line and on which side for traffic each way.
export function streetsNear(area, ways, withinFt = 220) {
  const F = frame(centroid(area)), c = { x: 0, y: 0 };
  const out = [];
  for (const w of ways) {
    if (w.tags.highway === 'service' && !w.tags.name) continue;
    const pts = w.geom.map(F.to), cum = measure(pts), p = project(pts, cum, c);
    if (p.d > withinFt) continue;
    const key = nameKey(w);
    const prev = out.find(o => o.key === key);
    if (prev && prev.d <= p.d) continue;
    if (prev) out.splice(out.indexOf(prev), 1);
    out.push({ key, way: w, d: p.d });
  }
  out.sort((a, b) => a.d - b.d);
  return out.slice(0, 6).map((o, i) => ({
    n: i + 1, wayId: o.way.id, key: o.key, name: o.way.tags.name || o.way.tags.ref || `(unnamed ${o.way.tags.highway})`,
    highway: o.way.tags.highway, lanes: lanesOf(o.way), oneway: isOneway(o.way), maxspeed_mph: mph(o.way),
    centre_line_ft: Math.round(o.d),
  }));
}

// The approach for one street: travel direction (worked out from the side of the centre line the work
// is on — right-hand traffic — or the one-way direction), and the traced road ending where the work
// starts, offset to the lane centre or the edge line. Returns { road, direction, lanesEachWay, offset }.
export function traceApproach({ area, ways, wayId, setup = 'lane_closed', side = 'right', speed = 35, roadType = 'urban_low', laneWidth = 12, shoulderWidth = 8 }) {
  const start = ways.find(w => w.id === wayId);
  if (!start || !Array.isArray(area) || area.length < 3) return null;
  const F = frame(centroid(area));
  let pts = chainFor(ways, start).map(F.to), cum = measure(pts);
  const one = isOneway(start);
  const c = project(pts, cum, { x: 0, y: 0 });
  // Two-way: the work is on the half whose traffic it is to the right of.
  if (!one && c.off < 0) { pts = pts.slice().reverse(); cum = measure(pts); }
  const areaPts = area.map(F.to).map(q => project(pts, cum, q));
  const sStart = Math.min(...areaPts.map(p => p.s));
  const lanes = lanesOf(start), each = one ? lanes : Math.max(1, Math.floor(lanes / 2));
  const W = Number(laneWidth) || 12;
  // Offsets from the mapped centre line, + = right of travel.
  let off;
  if (setup === 'shoulder') off = side === 'left' && one ? -(lanes / 2) * W : (one ? lanes / 2 : each) * W;
  else if (one) off = side === 'left' ? -((lanes - 1) / 2) * W : ((lanes - 1) / 2) * W;
  else off = side === 'left' ? W / 2 : (each - 0.5) * W;
  const n = tcpNumbers({ setup, speed, roadType, laneWidth: W, shoulderWidth });
  const reach = n.buffer + n.taper + n.A + n.B + (setup === 'shoulder' ? 0 : n.C) + 250;
  const s0 = Math.max(0, sStart - reach);
  const ss = [s0, ...cum.filter(s => s > s0 && s < sStart), sStart];
  const road = ss.map(s => F.back(pointAt(pts, cum, s, off)));
  const a = pointAt(pts, cum, Math.max(0, sStart - 200), 0), b = pointAt(pts, cum, sStart, 0);
  const bearing = (Math.atan2(b.x - a.x, b.y - a.y) * 180) / Math.PI;
  return { road, direction: compass(bearing), lanesEachWay: each, oneway: one, offsetFromCentre: Math.round(Math.abs(c.off)), mappedFt: Math.round(sStart - s0) };
}

// The traced road moved sideways by `ft` (+ = to the right of travel) — the AI check's "the cones are
// a few feet off the lane line" fix. Each point moves along the line's normal there.
export function shiftRoad(road, ft) {
  if (!Array.isArray(road) || road.length < 2 || !ft) return road;
  const F = frame(road[road.length - 1]);
  const pts = road.map(F.to);
  return pts.map((p, i) => {
    const a = pts[Math.max(0, i - 1)], b = pts[Math.min(pts.length - 1, i + 1)];
    const L = Math.hypot(b.x - a.x, b.y - a.y) || 1, ux = (b.x - a.x) / L, uy = (b.y - a.y) / L;
    return F.back({ x: p.x + uy * ft, y: p.y - ux * ft });
  });
}

// Side streets and driveways that meet the traced approach or run beside the work (for the AI check):
// [{ name, kind, ftBeforeWork }] — negative = past the start of the work.
export function crossingsAlong(road, area, ways, mainName) {
  if (!Array.isArray(road) || road.length < 2) return [];
  const F = frame(road[road.length - 1]);
  const pts = road.map(F.to), cum = measure(pts), total = cum[cum.length - 1];
  const workPts = (area || []).map(F.to);
  const out = [];
  for (const w of ways) {
    if (nameKey(w) === mainName) continue;
    let best = null;
    for (const ll of w.geom) {
      const q = F.to(ll), p = project(pts, cum, q);
      if (p.d < 45 && (!best || p.d < best.d)) best = { d: p.d, s: p.s };
    }
    // Not on the approach, but touching the work area itself (beside or past the start of the work).
    if (!best && workPts.length && w.geom.some(ll => { const q = F.to(ll); return workPts.some(z => Math.hypot(z.x - q.x, z.y - q.y) < 60); })) best = { d: 0, s: total + 1 };
    if (!best) continue;
    const name = w.tags.name || (w.tags.service === 'driveway' ? 'a driveway' : w.tags.highway === 'service' ? 'a service drive / parking entrance' : `an unnamed ${w.tags.highway}`);
    if (out.some(o => o.name === name && Math.abs(o.ftBeforeWork - Math.round(total - best.s)) < 80)) continue;
    out.push({ name, kind: w.tags.highway, ftBeforeWork: Math.round(total - best.s) });
  }
  return out.sort((a, b) => b.ftBeforeWork - a.ftBeforeWork).slice(0, 12);
}
